import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { INSIGHTS_URL } from '@marmon/frontend/common';
import {
	Insight,
	InsightCacheResponse,
	InsightGenerateRequest,
	InsightGenerateResponse,
	InsightPollResponse,
	InsightsApiPath,
	KristaProduct,
	SummaryTargetId,
} from '@marmon/shared/types/krista';
import { Observable } from 'rxjs';

/**
 * Frontend client for Vizashift insight generation.
 * Talks only to our API; the API owns Krista calls and response caching.
 */
@Injectable({ providedIn: 'root' })
export class InsightApiService {
	private readonly http = inject(HttpClient);
	private readonly baseUrl = inject(INSIGHTS_URL, { optional: true }) ?? '';

	generate(
		request: InsightGenerateRequest,
	): Observable<InsightGenerateResponse> {
		return this.http.post<InsightGenerateResponse>(this.url(), request);
	}

	generateDriverReport(
		analysisId: string,
		calculationId: string,
		targetId: string,
	): Observable<InsightGenerateResponse> {
		return this.generate({
			analysisId,
			calculationId,
			product: 'driver_report',
			targetId: targetId,
		});
	}

	lookupDriverReport(
		analysisId: string,
		calculationId: string,
		targetId: string,
	): Observable<InsightCacheResponse> {
		return this.http.get<InsightCacheResponse>(
			`${this.url()}/${analysisId}/${calculationId}`,
			{ params: { product: 'driver_report', targetId: targetId } },
		);
	}

	generateSummary(
		analysisId: string,
		calculationId: string,
	): Observable<InsightGenerateResponse> {
		return this.generate({
			analysisId,
			calculationId,
			product: 'summary',
			targetId: SummaryTargetId,
		});
	}

	lookupSummary(
		analysisId: string,
		calculationId: string,
	): Observable<InsightCacheResponse> {
		return this.http.get<InsightCacheResponse>(
			`${this.url()}/${analysisId}/${calculationId}`,
			{ params: { product: 'summary', targetId: SummaryTargetId } },
		);
	}

	generateQbr(
		analysisId: string,
		calculationId: string,
	): Observable<InsightGenerateResponse> {
		return this.generateProduct('qbr', analysisId, calculationId);
	}

	generateReportSlide(
		analysisId: string,
		calculationId: string,
	): Observable<InsightGenerateResponse> {
		return this.generateProduct('report', analysisId, calculationId);
	}

	get(
		analysisId: string,
		calculationId: string,
		insightId: string,
	): Observable<Insight> {
		return this.http.get<Insight>(
			`${this.url()}/${analysisId}/${calculationId}/${insightId}`,
		);
	}

	poll(
		analysisId: string,
		calculationId: string,
		insightId: string,
	): Observable<InsightPollResponse> {
		return this.http.post<InsightPollResponse>(
			`${this.url()}/${analysisId}/${calculationId}/${insightId}/poll`,
			{},
		);
	}

	private generateProduct(
		product: KristaProduct,
		analysisId: string,
		calculationId: string,
	): Observable<InsightGenerateResponse> {
		return this.generate({ analysisId, calculationId, product });
	}

	private url(): string {
		return `${this.baseUrl.replace(/\/$/, '')}${InsightsApiPath}`;
	}
}
